/**
 * Lost in Labyrinth — Main entry point.
 *
 * Creates the Phaser.Game instance with all scenes registered
 * and global configuration applied.
 *
 * On mobile/touch-only devices the game is not started — instead
 * a lore-themed HTML overlay is shown explaining that a keyboard
 * is required (see #mobile-block in index.html).
 */

import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from './config/game-config.js';
import BootScene from './scenes/BootScene.js';
import TitleScene from './scenes/TitleScene.js';
import HelpScene from './scenes/HelpScene.js';
import ShopScene from './scenes/ShopScene.js';
import ConfigScene from './scenes/ConfigScene.js';
import ManuscriptsScene from './scenes/ManuscriptsScene.js';
import AchievementsScene from './scenes/AchievementsScene.js';
import RecordsScene from './scenes/RecordsScene.js';
import GameScene from './scenes/GameScene.js';
import HudScene from './scenes/HudScene.js';
import GameOverScene from './scenes/GameOverScene.js';

// ---------------------------------------------------------------
// Mobile detection — block game before Phaser even initialises
// ---------------------------------------------------------------

/**
 * Detect whether the current device is a mobile/touch-only device
 * that lacks a physical keyboard.
 *
 * @returns {boolean}
 */
function isMobileDevice() {
  // 1. Coarse pointer without fine pointer (most reliable modern check)
  if (window.matchMedia
      && window.matchMedia('(pointer: coarse)').matches
      && !window.matchMedia('(pointer: fine)').matches) {
    return true;
  }
  // 2. User-agent fallback
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '')) {
    return true;
  }
  // 3. Touch + small viewport heuristic
  if ('ontouchstart' in window && window.innerWidth < 900 && window.innerHeight < 700) {
    return true;
  }
  return false;
}

if (isMobileDevice()) {
  // Hide the loading overlay and show the mobile-block overlay instead
  const loadingOverlay = document.getElementById('loading-overlay');
  if (loadingOverlay) loadingOverlay.remove();

  const mobileBlock = document.getElementById('mobile-block');
  if (mobileBlock) mobileBlock.classList.add('visible');
} else {
  // Desktop — boot the game normally
  const config = {
    type: Phaser.AUTO,
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    parent: 'game-container',
    backgroundColor: '#000000',
    pixelArt: true,

    physics: {
      default: 'arcade',
      arcade: {
        gravity: { x: 0, y: 0 },
        debug: false,
      },
    },

    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },

    scene: [
      BootScene,
      TitleScene,
      HelpScene,
      ShopScene,
      ConfigScene,
      ManuscriptsScene,
      AchievementsScene,
      RecordsScene,
      GameScene,
      HudScene,
      GameOverScene,
    ],
  };

  new Phaser.Game(config);
}
